import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import useTableSort from '../../hooks/useTableSort';
import { FilterTemplateDropdown, FilterIcon, useFilterTemplate } from '../common/FilteringTemplate';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { toSharePointHref } from '../../utils/sharepointUrl';

const EstimateManagement = ({ isSuperadmin }) => {
  const { instance, accounts } = useMsal();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Which set of estimates to show: active (default), archived, or all. Superadmin only.
  const [estimateView, setEstimateView] = useState('active');

  const estColumns = useMemo(() => [
    { key: 'estimate_number', label: 'Estimate Number', type: 'text' },
    { key: 'estimate_name', label: 'Estimate Name', type: 'text' },
    { key: 'client_name', label: 'Client', type: 'text' },
    { key: 'division_display', label: 'Division', type: 'text' },
    { key: 'disciplines_display', label: 'Disciplines', type: 'text' },
    { key: 'created_display', label: 'Created', type: 'date' },
    { key: 'awarded_display', label: 'Awarded', type: 'text' },
    { key: 'job_number', label: 'Job Number', type: 'text' },
  ], []);

  const estSort = useTableSort({ columns: estColumns });

  const flatEstimates = useMemo(() => estimates.map(e => ({
    ...e,
    division_display: e.division_name ? `${e.division_number} - ${e.division_name}` : '',
    disciplines_display: e.disciplines?.length > 0 ? e.disciplines.map(d => d.discipline_code).join(', ') : '',
    created_display: e.created_at ? new Date(e.created_at).toLocaleDateString() : '',
    awarded_display: e.awarded ? 'Yes' : 'No',
  })), [estimates]);

  const estFilter = useFilterTemplate({ items: flatEstimates, columns: estColumns });

  const filteredEstimates = useMemo(() => {
    let result = estFilter.filteredItems;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const compact = q.replace(/[-\s]/g, '');
      result = result.filter(e =>
        e.estimate_number?.toLowerCase().includes(q) ||
        e.estimate_number?.toLowerCase().replace(/[-\s]/g, '').includes(compact) ||
        e.estimate_name?.toLowerCase().includes(q) ||
        e.client_name?.toLowerCase().includes(q) ||
        e.division_display?.toLowerCase().includes(q) ||
        e.disciplines_display?.toLowerCase().includes(q) ||
        e.job_number?.toLowerCase().includes(q) ||
        e.created_display?.toLowerCase().includes(q) ||
        e.awarded_display?.toLowerCase().includes(q)
      );
    }
    return estSort.applySorting(result);
  }, [estFilter.filteredItems, searchTerm, estSort]);

  const fetchEstimates = useCallback(async () => {
    try {
      setLoading(true);
      const url = estimateView === 'active' ? '/estimates' : `/estimates?archived=${estimateView}`;
      const res = await authenticatedApiCall(instance, accounts, 'GET', url);
      setEstimates(res.data.estimates || []);
    } catch (error) {
      console.error('Error fetching estimates:', error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, estimateView]);

  const fetchMeta = useCallback(async () => {
    try {
      const [divRes, discRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/divisions'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/disciplines')
      ]);
      setDivisions(divRes.data.data || []);
      setDisciplines(discRes.data.data || []);
    } catch (error) {
      console.error('Error fetching meta data:', error);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchEstimates();
    fetchMeta();
  }, [fetchEstimates, fetchMeta]);

  const handleManage = (estimate) => {
    setSelectedEstimate(estimate);
    setShowManageModal(true);
  };

  const handleAward = (estimate) => {
    setSelectedEstimate(estimate);
    setShowAwardModal(true);
  };

  const handleDelete = (estimate) => {
    setSelectedEstimate(estimate);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/estimates/${selectedEstimate.id}`);
      setEstimates(prev => prev.filter(e => e.id !== selectedEstimate.id));
      setShowDeleteConfirm(false);
      setSelectedEstimate(null);
    } catch (error) {
      console.error('Error deleting estimate:', error);
    }
  };

  const handleArchive = async (estimate) => {
    if (!window.confirm(`Archive estimate "${estimate.estimate_number} - ${estimate.estimate_name}"?\n\nIt will be hidden from the active list, but no data is deleted. You can restore it at any time.`)) {
      return;
    }
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/estimates/${estimate.id}/archive`);
      fetchEstimates();
    } catch (error) {
      console.error('Error archiving estimate:', error);
      window.alert(error.response?.data?.message || 'Failed to archive estimate');
    }
  };

  const handleRestore = async (estimate) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/estimates/${estimate.id}/restore`);
      fetchEstimates();
    } catch (error) {
      console.error('Error restoring estimate:', error);
      window.alert(error.response?.data?.message || 'Failed to restore estimate');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>Estimate Management</h3>
          <button
            onClick={() => setShowHelp(true)}
            style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: '#6c757d', color: 'white', border: 'none',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            title="How estimates work"
          >
            ?
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {isSuperadmin && (
            <div style={{ display: 'inline-flex', border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden' }}>
              {[
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
                { key: 'all', label: 'All' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setEstimateView(opt.key)}
                  style={{
                    padding: '0.45rem 0.85rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                    backgroundColor: estimateView === opt.key ? '#007bff' : 'white',
                    color: estimateView === opt.key ? 'white' : '#495057',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <input
            type="text"
            placeholder="Search estimates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.45rem 0.75rem', border: '1px solid #ced4da', borderRadius: '4px',
              fontSize: '0.9rem', width: '240px', boxSizing: 'border-box'
            }}
          />
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white',
              border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem'
            }}
          >
            + Create Estimate
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          Loading estimates...
        </div>
      ) : estimates.length === 0 ? (
        <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <p style={{ color: '#666' }}>No estimates found.</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>Click "Create Estimate" to add your first estimate.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowY: 'scroll', overflowX: 'auto', height: '540px' }}>
          {estFilter.getActiveFilters().length > 0 && (
            <div style={{ padding: '0.5rem 1rem', backgroundColor: '#d1fae5', borderBottom: '1px solid #c3e6cb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
              <span style={{ color: '#065f46' }}>
                {estFilter.getActiveFilters().length} active filter(s) — showing {filteredEstimates.length} of {estimates.length}
              </span>
              <button onClick={() => estFilter.clearAllFilters()} style={{ padding: '2px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Clear All Filters</button>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                {estColumns.map(col => (
                  <th key={col.key} style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span onClick={() => estSort.handleSort(col.key)} style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }} title={`Sort by ${col.label}`}>
                        {col.label}
                        {estSort.sortColumn === col.key && (<span style={{ fontSize: '0.7rem', marginLeft: '3px' }}>{estSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                      </span>
                      <span onClick={(e) => estFilter.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <FilterIcon isActive={!!(estFilter.columnFilters[col.key]?.length > 0 || estFilter.filterText[col.key])} color="#666" />
                      </span>
                    </div>
                    {estFilter.openFilterColumn === col.key && (
                      <FilterTemplateDropdown
                        columnKey={col.key}
                        columnLabel={col.label}
                        isDateColumn={col.type === 'date'}
                        columnFilters={estFilter.columnFilters}
                        filterMode={estFilter.filterMode}
                        filterText={estFilter.filterText}
                        filterSearch={estFilter.filterSearch}
                        filterButtonPositions={estFilter.filterButtonPositions}
                        setOpenFilterColumn={estFilter.setOpenFilterColumn}
                        setFilterSearch={estFilter.setFilterSearch}
                        getUniqueValues={estFilter.getUniqueValues}
                        handleFilterToggle={estFilter.handleFilterToggle}
                        selectAllColumnFilter={estFilter.selectAllColumnFilter}
                        clearColumnFilter={estFilter.clearColumnFilter}
                        applyTextFilter={estFilter.applyTextFilter}
                      />
                    )}
                  </th>
                ))}
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEstimates.map(est => (
                <tr key={est.id}>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    <strong>{est.estimate_number}</strong>
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {est.estimate_name}
                    {est.archived_at && (
                      <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', backgroundColor: '#e9ecef', color: '#6c757d', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>
                        Archived
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {est.client_name ? est.client_name : (
                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {est.division_display ? (
                      <span style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6c757d', color: 'white', borderRadius: '4px', fontSize: '0.75rem' }}>
                        {est.division_display}
                      </span>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {est.disciplines && est.disciplines.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {est.disciplines.map(disc => (
                          <span key={disc.id} style={{ padding: '0.125rem 0.375rem', backgroundColor: '#17a2b8', color: 'white', borderRadius: '4px', fontSize: '0.7rem' }}>
                            {disc.discipline_code}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                    {est.created_display}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {est.awarded ? (
                      <span style={{ padding: '0.2rem 0.6rem', backgroundColor: '#28a745', color: 'white', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>
                        Yes
                      </span>
                    ) : (
                      <span style={{ padding: '0.2rem 0.6rem', backgroundColor: '#ffc107', color: '#333', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>
                        No
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    {estimateJobNumbers(est).length > 0 ? (
                      <strong style={{ color: '#007bff' }}>{estimateJobNumbers(est).join(', ')}</strong>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap' }}>
                      {est.sharepoint_url && (
                        <a href={toSharePointHref(est.sharepoint_url)} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                          title="Open SharePoint folder">
                          SharePoint
                        </a>
                      )}
                      <button onClick={() => handleManage(est)}
                        style={{ padding: '0.25rem 0.5rem', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        Manage
                      </button>
                      {isSuperadmin && (est.archived_at ? (
                        <button onClick={() => handleRestore(est)}
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                          Restore
                        </button>
                      ) : (
                        <button onClick={() => handleArchive(est)}
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                          Archive
                        </button>
                      ))}
                      {isSuperadmin && (
                        <button onClick={() => handleDelete(est)}
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                          Delete
                        </button>
                      )}
                      {!est.archived_at && (
                        <button onClick={() => handleAward(est)}
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {est.awarded ? 'Award another' : 'Award'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredEstimates.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No estimates match your search or filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Estimate Modal */}
      {showCreateModal && (
        <EstimateFormModal
          isOpen={true}
          onClose={() => { setShowCreateModal(false); setSelectedEstimate(null); }}
          estimate={null}
          divisions={divisions}
          disciplines={disciplines}
          onSaved={(saved) => {
            setEstimates(prev => [saved, ...prev]);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Manage / Details Modal (like ProjectDetailsModal) */}
      {showManageModal && selectedEstimate && (
        <EstimateDetailsModal
          isOpen={true}
          onClose={() => { setShowManageModal(false); setSelectedEstimate(null); }}
          estimate={selectedEstimate}
          onEstimateUpdated={() => fetchEstimates()}
          onAwardAnother={() => {
            setShowManageModal(false);
            setShowAwardModal(true);
          }}
        />
      )}

      {/* Award Modal */}
      {showAwardModal && selectedEstimate && (
        <AwardEstimateModal
          isOpen={true}
          onClose={() => { setShowAwardModal(false); setSelectedEstimate(null); }}
          estimate={selectedEstimate}
          onAwarded={(result) => {
            const jobs = result.estimate?.job_numbers?.length
              ? result.estimate.job_numbers
              : [...new Set([...estimateJobNumbers(selectedEstimate), result.project.job_number].filter(Boolean))];
            setEstimates(prev => prev.map(e => e.id === selectedEstimate.id
              ? {
                  ...e,
                  awarded: true,
                  job_number: result.estimate?.job_number || e.job_number || result.project.job_number,
                  job_numbers: jobs,
                }
              : e
            ));
            setShowAwardModal(false);
            setSelectedEstimate(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedEstimate && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalBoxStyle, maxWidth: '450px' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Delete Estimate</h3>
            <p style={{ color: '#666' }}>
              Are you sure you want to delete estimate <strong>{selectedEstimate.estimate_number}</strong> — "{selectedEstimate.estimate_name}"?
            </p>
            {selectedEstimate.awarded && (
              <p style={{ color: '#dc3545', fontSize: '0.9rem', fontWeight: '500' }}>
                Warning: This estimate has been awarded. The associated projects will NOT be deleted.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button onClick={() => { setShowDeleteConfirm(false); setSelectedEstimate(null); }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmDelete}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalBoxStyle, maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Estimate Workflow</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="estimate-management-help" title="Estimate Workflow Help" />
                <button onClick={() => setShowHelp(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#888' }}>
                  ×
                </button>
              </div>
            </div>
            <div style={{ color: '#555', lineHeight: '1.7' }}>
              <HelpWithVideoTour guideId="estimate-management-help" title="Estimate Workflow Help" accent="#0369a1">
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>1.</div>
                <div>
                  <strong>Create an Estimate</strong><br/>
                  Enter the estimate number, name, select a division and applicable disciplines. The estimate number drives the Estimating department work.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>2.</div>
                <div>
                  <strong>Work on the Estimate</strong><br/>
                  The estimating team works on preparing the bid. The estimate remains in "Not Awarded" status during this period.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>3.</div>
                <div>
                  <strong>Award the Contract</strong><br/>
                  When the contract is awarded, click Award (there is no Edit on this list — use Manage for name and people). Enter the job number, read back the estimate name and client, and type the job number again. The portal creates a project with the same name, division, and disciplines. The SharePoint job site does not have to exist yet. One estimate can be awarded again — a lump sum job and a time and material job — and each award creates another project. The first job number stays on the estimate. If the job number is right and the bid was wrong, a superadmin opens the job under Project Management and uses Change linked estimate. Changing the job number itself is only allowed while the job is still empty.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>4.</div>
                <div>
                  <strong>Project Ready</strong><br/>
                  The new project appears in the Projects tab, ready for tracking. All admin/superadmin users are automatically assigned.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffecb5' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>5.</div>
                <div>
                  <strong>Wrong bid on the right job</strong><br/>
                  This list has Manage, not Edit. If the job number is correct and the estimate was the neighbour, a superadmin opens the job under Project Management and presses Change linked estimate. People on the job stay. Change job number is only allowed while the job is still empty.
                </div>
              </div>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ===================== Estimate Form Modal (Create only) ===================== */

const EstimateFormModal = ({ isOpen, onClose, estimate, divisions, disciplines, onSaved }) => {
  const { instance, accounts } = useMsal();
  const [formData, setFormData] = useState({
    estimate_number: estimate?.estimate_number || '',
    estimate_name: estimate?.estimate_name || '',
    client_name: estimate?.client_name || '',
    division_id: estimate?.division_id || '',
    discipline_ids: estimate?.disciplines?.map(d => d.id) || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleDiscipline = (id) => {
    setFormData(prev => ({
      ...prev,
      discipline_ids: prev.discipline_ids.includes(id)
        ? prev.discipline_ids.filter(d => d !== id)
        : [...prev.discipline_ids, id]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.estimate_number.trim() || !formData.estimate_name.trim()) {
      setError('Estimate number and name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        estimate_number: formData.estimate_number.trim(),
        estimate_name: formData.estimate_name.trim(),
        client_name: formData.client_name.trim() || null,
        division_id: formData.division_id || null,
        discipline_ids: formData.discipline_ids
      };
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/estimates', payload);
      if (res.data.success) {
        onSaved(res.data.estimate);
      } else {
        setError(res.data.message || 'Failed to save');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{ ...modalBoxStyle, maxWidth: '650px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: '#333' }}>Create New Estimate</h3>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Estimate Number *</label>
          <input name="estimate_number" value={formData.estimate_number} onChange={handleChange}
            style={inputStyle} placeholder="e.g., EST-2026-001" />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Estimate Name *</label>
          <input name="estimate_name" value={formData.estimate_name} onChange={handleChange}
            style={inputStyle} placeholder="Enter estimate name" />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Client</label>
          <input name="client_name" value={formData.client_name} onChange={handleChange}
            style={inputStyle} placeholder="Enter client name (optional)" />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Division</label>
          <select name="division_id" value={formData.division_id} onChange={handleChange} style={selectStyle}>
            <option value="">-- Select Division (Optional) --</option>
            {divisions.map(div => (
              <option key={div.id} value={div.id}>{div.division_number} - {div.division_name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>
            Disciplines
            {formData.discipline_ids.length > 0 && (
              <span style={{ marginLeft: '0.5rem', padding: '0.15rem 0.4rem', backgroundColor: '#17a2b8', color: 'white', borderRadius: '4px', fontSize: '0.75rem' }}>
                {formData.discipline_ids.length} selected
              </span>
            )}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem' }}>
            {disciplines.map(disc => {
              const selected = formData.discipline_ids.includes(disc.id);
              return (
                <div key={disc.id} onClick={() => toggleDiscipline(disc.id)}
                  style={{
                    display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem',
                    border: `1px solid ${selected ? '#007bff' : '#e9ecef'}`,
                    backgroundColor: selected ? '#e7f3ff' : '#f8f9fa',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem'
                  }}>
                  <input type="checkbox" checked={selected} onChange={() => {}} style={{ marginRight: '0.5rem', accentColor: '#007bff' }} />
                  {disc.discipline_name}
                </div>
              );
            })}
          </div>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button onClick={onClose} disabled={saving} style={secondaryBtnStyle}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Creating...' : 'Create Estimate'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== Estimate Details Modal (Manage — like ProjectDetailsModal) ===================== */

const EstimateDetailsModal = ({ isOpen, onClose, estimate, onEstimateUpdated, onAwardAnother }) => {
  const { instance, accounts } = useMsal();
  const [activeTab, setActiveTab] = useState('details');

  const [estimateData, setEstimateData] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [disciplines, setDisciplines] = useState([]);

  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    estimate_number: '',
    estimate_name: '',
    client_name: '',
    division_id: '',
    discipline_ids: [],
    job_number: '',
    awarded: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchData = useCallback(async () => {
    if (!isOpen || !estimate) return;
    setLoading(true);
    setError('');
    try {
      const [estRes, divRes, discRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/estimates/${estimate.id}`),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/divisions'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/disciplines')
      ]);
      const est = estRes.data.estimate;
      setEstimateData(est);
      setDivisions(divRes.data.data || []);
      setDisciplines(discRes.data.data || []);
      setFormData({
        estimate_number: est.estimate_number || '',
        estimate_name: est.estimate_name || '',
        client_name: est.client_name || '',
        division_id: est.division_id || '',
        discipline_ids: est.disciplines?.map(d => d.id) || [],
        job_number: est.job_number || '',
        awarded: est.awarded || false
      });
    } catch (err) {
      console.error('Error fetching estimate data:', err);
      setError('Failed to load estimate data');
    } finally {
      setLoading(false);
    }

    try {
      const [usersRes, assignedRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/estimates/assignable-users'),
        authenticatedApiCall(instance, accounts, 'GET', `/estimates/${estimate.id}/assignments`)
      ]);
      setAllUsers(usersRes.data.users || []);
      setAssignedUsers(assignedRes.data.assignments || []);
    } catch (err) {
      console.error('Error fetching user assignments:', err);
    }
  }, [isOpen, estimate, instance, accounts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDisciplineToggle = (disciplineId) => {
    setFormData(prev => ({
      ...prev,
      discipline_ids: prev.discipline_ids.includes(disciplineId)
        ? prev.discipline_ids.filter(id => id !== disciplineId)
        : [...prev.discipline_ids, disciplineId]
    }));
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/estimates/${estimate.id}`, {
        estimate_number: formData.estimate_number,
        estimate_name: formData.estimate_name,
        client_name: formData.client_name || null,
        division_id: formData.division_id || null,
        discipline_ids: formData.discipline_ids,
        job_number: formData.job_number || null,
        awarded: formData.awarded
      });
      setSuccessMessage('Estimate updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchData();
      if (onEstimateUpdated) onEstimateUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update estimate');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = async (userId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/estimates/${estimate.id}/assign`, { user_id: userId });
      fetchData();
    } catch (err) {
      console.error('Error assigning user:', err);
      setError(err.response?.data?.message || 'Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/estimates/${estimate.id}/assign/${userId}`);
      fetchData();
    } catch (err) {
      console.error('Error unassigning user:', err);
      setError('Failed to unassign user');
    }
  };

  const filteredUnassignedUsers = allUsers.filter(user => {
    const isAssigned = assignedUsers.some(au => au.user_id === user.id);
    if (isAssigned) return false;
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      (user.display_name || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  const mdlStyles = {
    overlay: modalOverlayStyle,
    modal: {
      backgroundColor: '#fff', borderRadius: '12px', width: '90%', maxWidth: '950px',
      maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      display: 'flex', flexDirection: 'column'
    },
    header: {
      padding: '1.5rem 2rem', borderBottom: '1px solid #e9ecef',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa'
    },
    tabs: { display: 'flex', borderBottom: '1px solid #e9ecef', backgroundColor: '#fff' },
    tab: (isActive) => ({
      padding: '1rem 1.5rem', border: 'none', backgroundColor: 'transparent',
      borderBottom: isActive ? '2px solid #007bff' : '2px solid transparent',
      color: isActive ? '#007bff' : '#666', cursor: 'pointer', fontSize: '0.95rem',
      fontWeight: isActive ? '500' : '400', transition: 'all 0.2s ease'
    }),
    content: { padding: '2rem', overflowY: 'auto', flex: 1 },
    footer: {
      padding: '1rem 2rem', borderTop: '1px solid #e9ecef',
      display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', backgroundColor: '#f8f9fa'
    }
  };

  const renderDetailsTab = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Estimate Number</label>
          <input type="text" name="estimate_number" value={formData.estimate_number} onChange={handleInputChange}
            style={inputStyle} />
        </div>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Estimate Name</label>
          <input type="text" name="estimate_name" value={formData.estimate_name} onChange={handleInputChange} style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Client</label>
          <input type="text" name="client_name" value={formData.client_name} onChange={handleInputChange}
            placeholder="Enter client name (optional)" style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Division</label>
          <select name="division_id" value={formData.division_id} onChange={handleInputChange} style={selectStyle}>
            <option value="">-- No Division --</option>
            {divisions.map(div => (
              <option key={div.id} value={div.id}>{div.division_number} - {div.division_name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Awarded</label>
          <input
            type="text"
            value={formData.awarded ? 'Yes' : 'No'}
            disabled
            style={{ ...selectStyle, backgroundColor: '#e9ecef', cursor: 'not-allowed', color: '#495057' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Job Number</label>
          <input type="text" name="job_number" value={formData.job_number} disabled
            placeholder="Set by Award"
            style={{ ...inputStyle, backgroundColor: '#e9ecef', cursor: 'not-allowed', color: '#495057' }} />
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
            If the job was awarded to the wrong estimate, open the job under Project Management and use Change linked estimate. Superadmin only. There is no Edit on this list — use Manage for name, client, and people.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label style={labelStyle}>Disciplines</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem' }}>
          {disciplines.map(disc => {
            const selected = formData.discipline_ids.includes(disc.id);
            return (
              <div key={disc.id} onClick={() => handleDisciplineToggle(disc.id)}
                style={{
                  display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem',
                  backgroundColor: selected ? '#e7f3ff' : '#f8f9fa', borderRadius: '6px',
                  cursor: 'pointer', border: `1px solid ${selected ? '#007bff' : '#e9ecef'}`,
                  transition: 'all 0.2s ease'
                }}>
                <input type="checkbox" checked={selected} onChange={() => {}}
                  style={{ marginRight: '0.5rem', width: '16px', height: '16px', accentColor: '#007bff' }} />
                <span style={{ color: '#333', fontSize: '0.9rem' }}>{disc.discipline_name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const renderInfoTab = () => (
    <div>
      <h4 style={{ margin: '0 0 1rem', color: '#333', fontSize: '1rem', fontWeight: '600' }}>Estimate Information</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <InfoCard label="Estimate Number" value={estimateData?.estimate_number} />
        <InfoCard label="Estimate Name" value={estimateData?.estimate_name} />
        <InfoCard label="Client" value={estimateData?.client_name} />
        <InfoCard label="Division" value={estimateData?.division_name ? `${estimateData.division_number} - ${estimateData.division_name}` : 'None'} />
        <InfoCard label="Created" value={estimateData?.created_at ? new Date(estimateData.created_at).toLocaleString() : '—'} />
        <InfoCard label="Last Updated" value={estimateData?.updated_at ? new Date(estimateData.updated_at).toLocaleString() : '—'} />
        <InfoCard label="Awarded" value={estimateData?.awarded ? 'Yes' : 'No'} highlight={estimateData?.awarded ? '#28a745' : '#ffc107'} />
        {estimateData?.awarded && (
          <InfoCard
            label={estimateJobNumbers(estimateData).length > 1 ? 'Linked Job Numbers' : 'Linked Job Number'}
            value={estimateJobNumbers(estimateData).join(', ')}
            highlight="#007bff"
          />
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', fontWeight: 500 }}>SharePoint Folder</div>
            {estimateData?.sharepoint_url ? (
              <a href={toSharePointHref(estimateData.sharepoint_url)} target="_blank" rel="noopener noreferrer"
                style={{ color: '#0d6efd', textDecoration: 'none', fontSize: '0.9rem', wordBreak: 'break-all' }}>
                Open estimate folder ↗
              </a>
            ) : (
              <span style={{ color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
                No link yet — filled overnight from Active Estimates (kept after archive/move).
              </span>
            )}
          </div>
        </div>
      </div>
      {estimateData?.disciplines && estimateData.disciplines.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <label style={{ ...labelStyle, marginBottom: '0.5rem' }}>Disciplines</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {estimateData.disciplines.map(d => (
              <span key={d.id} style={{ padding: '0.3rem 0.6rem', backgroundColor: '#17a2b8', color: 'white', borderRadius: '4px', fontSize: '0.8rem' }}>
                {d.discipline_code} — {d.discipline_name}
              </span>
            ))}
          </div>
        </div>
      )}
      {estimateData?.awarded && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
          <p style={{ margin: '0 0 0.75rem', color: '#2e7d32', fontSize: '0.9rem' }}>
            This estimate has been awarded to <strong>{estimateJobNumbers(estimateData).join(', ')}</strong>.
            Award it again to create another project from the same bid, for example a lump sum job and a time and material job. The existing projects stay as they are.
          </p>
          {onAwardAnother && !estimateData.archived_at && (
            <button onClick={onAwardAnother}
              style={{ padding: '0.4rem 0.8rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
              Award another job
            </button>
          )}
        </div>
      )}
    </div>
  );

  const userListStyles = {
    list: { border: '1px solid #e9ecef', borderRadius: '8px', maxHeight: '300px', overflowY: 'auto' },
    item: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0'
    },
    name: { fontWeight: '500', color: '#333', fontSize: '0.9rem' },
    email: { color: '#888', fontSize: '0.8rem', marginTop: '2px' },
    btnAssign: {
      padding: '4px 12px', fontSize: '0.8rem', border: 'none', borderRadius: '4px',
      cursor: 'pointer', backgroundColor: '#28a745', color: '#fff', fontWeight: '500'
    },
    btnRemove: {
      padding: '4px 12px', fontSize: '0.8rem', border: 'none', borderRadius: '4px',
      cursor: 'pointer', backgroundColor: '#dc3545', color: '#fff', fontWeight: '500'
    },
    search: {
      width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px',
      fontSize: '0.9rem', marginBottom: '0.75rem', outline: 'none'
    }
  };

  const renderUsersTab = () => (
    <>
      <h4 style={{ margin: '0 0 1rem', color: '#333', fontSize: '1rem', fontWeight: '600' }}>
        Assigned Users ({assignedUsers.length})
      </h4>
      {assignedUsers.length === 0 ? (
        <p style={{ color: '#666', fontStyle: 'italic' }}>No users assigned to this estimate</p>
      ) : (
        <div style={userListStyles.list}>
          {assignedUsers.map(au => (
            <div key={au.user_id} style={userListStyles.item}>
              <div>
                <div style={userListStyles.name}>{au.display_name}</div>
                <div style={userListStyles.email}>{au.email}</div>
              </div>
              <button onClick={() => handleUnassignUser(au.user_id)} style={userListStyles.btnRemove}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <h4 style={{ margin: '1.5rem 0 0.75rem', color: '#333', fontSize: '1rem', fontWeight: '600' }}>Add Users</h4>
      <input
        type="text"
        placeholder="Search users by name or email..."
        value={userSearchTerm}
        onChange={(e) => setUserSearchTerm(e.target.value)}
        style={userListStyles.search}
      />
      <div style={userListStyles.list}>
        {filteredUnassignedUsers.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
            {userSearchTerm ? 'No users found' : 'All users are assigned'}
          </p>
        ) : (
          filteredUnassignedUsers.slice(0, 20).map(user => (
            <div key={user.id} style={userListStyles.item}>
              <div>
                <div style={userListStyles.name}>{user.display_name}</div>
                <div style={userListStyles.email}>{user.email}</div>
              </div>
              <button onClick={() => handleAssignUser(user.id)} style={userListStyles.btnAssign}>
                + Assign
              </button>
            </div>
          ))
        )}
        {filteredUnassignedUsers.length > 20 && (
          <p style={{ color: '#666', fontSize: '0.85rem', padding: '0.5rem 1rem', textAlign: 'center' }}>
            Showing first 20 results. Use search to find more.
          </p>
        )}
      </div>
      <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#888' }}>
        Only assigned users with the Estimating department can see this estimate. Superadmins see all estimates.
      </p>
    </>
  );

  return (
    <div style={mdlStyles.overlay}>
      <div style={mdlStyles.modal}>
        <div style={mdlStyles.header}>
          <div>
            <h3 style={{ margin: 0, color: '#333', fontSize: '1.25rem', fontWeight: '600' }}>Estimate Settings</h3>
            <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
              {estimateData?.estimate_number} - {estimateData?.estimate_name}
            </p>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        <div style={mdlStyles.tabs}>
          <button style={mdlStyles.tab(activeTab === 'details')} onClick={() => setActiveTab('details')}>Details</button>
          <button style={mdlStyles.tab(activeTab === 'users')} onClick={() => setActiveTab('users')}>Users ({assignedUsers.length})</button>
          <button style={mdlStyles.tab(activeTab === 'info')} onClick={() => setActiveTab('info')}>Summary</button>
        </div>

        <div style={mdlStyles.content}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading estimate data...</div>
          ) : (
            <>
              {error && <div style={errorStyle}>{error}</div>}
              {successMessage && <div style={successStyle}>{successMessage}</div>}
              {activeTab === 'details' && renderDetailsTab()}
              {activeTab === 'users' && renderUsersTab()}
              {activeTab === 'info' && renderInfoTab()}
            </>
          )}
        </div>

        <div style={mdlStyles.footer}>
          <button onClick={onClose} style={secondaryBtnStyle}>Close</button>
          {activeTab === 'details' && (
            <button onClick={handleSaveDetails} disabled={saving}
              style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, highlight }) => (
  <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{label}</div>
    <div style={{ fontSize: '0.95rem', fontWeight: '500', color: highlight || '#333' }}>{value || '—'}</div>
  </div>
);

/* ===================== Award Estimate Modal ===================== */

function estimateJobNumbers(est) {
  if (Array.isArray(est?.job_numbers) && est.job_numbers.length) return est.job_numbers;
  return est?.job_number ? [est.job_number] : [];
}

const AwardEstimateModal = ({ isOpen, onClose, estimate, onAwarded }) => {
  const { instance, accounts } = useMsal();
  const [jobNumber, setJobNumber] = useState(estimate?.awarded ? '' : (estimate?.estimate_number || ''));
  const [confirmJob, setConfirmJob] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleAward = async () => {
    if (!jobNumber.trim()) {
      setError('Job number is required');
      return;
    }
    if (confirmJob.trim() !== jobNumber.trim()) {
      setError('Type the same job number again to confirm you are awarding this bid, not a neighbour with a similar name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/estimates/${estimate.id}/award`, {
        job_number: jobNumber.trim()
      });
      if (res.data.success) {
        onAwarded(res.data);
      } else {
        setError(res.data.message || 'Failed to award');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to award estimate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{ ...modalBoxStyle, maxWidth: '500px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: '#333' }}>{estimate.awarded ? 'Award another job' : 'Award Estimate'}</h3>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        <div style={{ padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '6px', marginBottom: '1.5rem', border: '1px solid #c8e6c9' }}>
          <p style={{ margin: 0, color: '#2e7d32', fontSize: '0.9rem' }}>
            {estimate.awarded
              ? `This estimate already has ${estimateJobNumbers(estimate).join(', ') || 'a project'}. This creates another project from the same estimate and leaves the existing one as it is.`
              : 'Awarding this estimate will automatically create a new project with the same name, division, and disciplines.'}
          </p>
        </div>

        <div style={{ marginBottom: '0.5rem' }}>
          <p style={{ color: '#555', margin: '0 0 0.5rem' }}>
            <strong>Estimate:</strong> {estimate.estimate_number} — {estimate.estimate_name}
          </p>
          {estimate.client_name && (
            <p style={{ color: '#555', margin: '0 0 0.5rem' }}>
              <strong>Client:</strong> {estimate.client_name}
            </p>
          )}
          {estimate.division_name && (
            <p style={{ color: '#555', margin: '0 0 0.5rem' }}>
              <strong>Division:</strong> {estimate.division_number} - {estimate.division_name}
            </p>
          )}
          {estimate.disciplines && estimate.disciplines.length > 0 && (
            <p style={{ color: '#555', margin: '0 0 0.5rem' }}>
              <strong>Disciplines:</strong> {estimate.disciplines.map(d => d.discipline_code).join(', ')}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
          <label style={labelStyle}>Job Number *</label>
          <input value={jobNumber} onChange={(e) => setJobNumber(e.target.value)}
            style={inputStyle} placeholder="Enter the job number for the new project" />
          <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.3rem 0 0' }}>
            {estimate.awarded
              ? 'A new job number. The estimate number is already used by the first project.'
              : 'This will become the project\'s job number. Defaults to the estimate number.'}
          </p>
        </div>

        {jobNumber.trim() && (
          <div style={{ padding: '0.85rem 1rem', backgroundColor: '#fff3cd', border: '1px solid #ffecb5', borderRadius: 6, marginBottom: '1rem' }}>
            <p style={{ margin: 0, color: '#664d03', fontSize: '0.9rem', lineHeight: 1.45 }}>
              You are about to create job <strong>{jobNumber.trim()}</strong> named{' '}
              <strong>{estimate.estimate_name}</strong>
              {estimate.client_name ? ` for ${estimate.client_name}` : ''} from estimate{' '}
              <strong>{estimate.estimate_number}</strong>. The job site in SharePoint does not have to exist yet — the name comes from this estimate.
            </p>
            <label style={{ ...labelStyle, marginTop: '0.75rem' }}>Type the job number again to confirm</label>
            <input value={confirmJob} onChange={(e) => setConfirmJob(e.target.value)}
              style={inputStyle} placeholder={jobNumber.trim()} />
          </div>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button onClick={onClose} disabled={saving} style={secondaryBtnStyle}>Cancel</button>
          <button onClick={handleAward} disabled={saving || !jobNumber.trim() || confirmJob.trim() !== jobNumber.trim()}
            style={{ padding: '0.5rem 1.25rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: (saving || !jobNumber.trim() || confirmJob.trim() !== jobNumber.trim()) ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.9rem', opacity: (saving || !jobNumber.trim() || confirmJob.trim() !== jobNumber.trim()) ? 0.6 : 1 }}>
            {saving ? 'Awarding...' : (estimate.awarded ? 'Award & Create Another Project' : 'Award & Create Project')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== Shared Styles ===================== */

const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
  justifyContent: 'center', alignItems: 'center', zIndex: 1000
};

const modalBoxStyle = {
  backgroundColor: 'white', borderRadius: '8px', padding: '2rem',
  width: '90%', maxHeight: '85vh', overflowY: 'auto',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
};

const closeButtonStyle = {
  background: 'none', border: 'none', fontSize: '1.5rem',
  cursor: 'pointer', color: '#666', padding: '0.25rem', lineHeight: 1
};

const labelStyle = {
  display: 'block', marginBottom: '0.5rem', fontWeight: '500',
  color: '#333', fontSize: '0.9rem'
};

const inputStyle = {
  width: '100%', padding: '0.75rem 1rem', border: '1px solid #ced4da',
  borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box'
};

const selectStyle = {
  width: '100%', padding: '0.75rem 1rem', border: '1px solid #ced4da',
  borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box', cursor: 'pointer'
};

const primaryBtnStyle = {
  padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white',
  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500'
};

const secondaryBtnStyle = {
  padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white',
  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem'
};

const errorStyle = {
  padding: '0.75rem 1rem', backgroundColor: '#f8d7da', color: '#721c24',
  borderRadius: '6px', marginBottom: '1rem', border: '1px solid #f5c6cb'
};

const successStyle = {
  padding: '0.75rem 1rem', backgroundColor: '#d4edda', color: '#155724',
  borderRadius: '6px', marginBottom: '1rem', border: '1px solid #c3e6cb'
};

export default EstimateManagement;
